import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { Supplier, SupplierParams, CreateSupplierDTO, UpdateSupplierDTO } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useSuppliers(initialParams?: SupplierParams) {
  const { supplierRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<Supplier> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<SupplierParams>(initialParams || { page: 1, limit: 20, activeOnly: true });

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await supplierRepo.getSuppliers(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, [supplierRepo, params]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const search = useCallback((searchTerm: string) => {
    setParams(prev => ({ ...prev, search: searchTerm, page: 1 }));
  }, []);

  const filterByCategory = useCallback((category: SupplierParams['category']) => {
    setParams(prev => ({ ...prev, category, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const createSupplier = useCallback(async (data: CreateSupplierDTO) => {
    const newSupplier = await supplierRepo.createSupplier(data);
    fetchSuppliers();
    return newSupplier;
  }, [supplierRepo, fetchSuppliers]);

  const updateSupplier = useCallback(async (id: string, data: UpdateSupplierDTO) => {
    const updated = await supplierRepo.updateSupplier(id, data);
    fetchSuppliers();
    return updated;
  }, [supplierRepo, fetchSuppliers]);

  const deleteSupplier = useCallback(async (id: string) => {
    await supplierRepo.deleteSupplier(id);
    fetchSuppliers();
  }, [supplierRepo, fetchSuppliers]);

  const getSupplierById = useCallback(async (id: string) => {
    return await supplierRepo.getSupplierById(id);
  }, [supplierRepo]);

  return {
    suppliers: data?.data || [],
    pagination: data?.meta,
    loading,
    error,
    params,
    search,
    filterByCategory,
    goToPage,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    getSupplierById,
    refresh: fetchSuppliers,
  };
}
